import { call } from 'redux-saga/effects';

import { GeneratorResult, SagaResult } from '@pressreader/appstore';
import { getBookInfo, mapBookDetailsFromDto } from '@pressreader/book-catalog-api';
import { findPublications } from '@pressreader/catalog-publications';
import { mapFromIssueInfoDto, mapFromPublicationDto } from '@pressreader/commerce-api';
import { AnyProduct, isProductBook, isProductPublicationSubscription, isProductSingleIssue } from '@pressreader/commerce-types';
import { getIssueInfo } from '@pressreader/content-api';
import { ExtractReturnType, isNonNullable } from '@pressreader/types';

function* loadMissingIssueInfo(products: AnyProduct[]): SagaResult<AnyProduct[]> {
    const singleIssueProduct = products.find(isProductSingleIssue);
    if (singleIssueProduct && !singleIssueProduct.issueInfo) {
        const issueInfoDto: ExtractReturnType<typeof getIssueInfo> = yield call(getIssueInfo, singleIssueProduct.issueKey);
        singleIssueProduct.issueInfo = mapFromIssueInfoDto(issueInfoDto);
    }

    return products;
}

function* loadMissingBookInfo(products: AnyProduct[]): SagaResult<AnyProduct[]> {
    const bookProduct = products.find(isProductBook);
    if (bookProduct && !bookProduct.bookInfo) {
        const bookInfoDto: ExtractReturnType<typeof getBookInfo> = yield call(getBookInfo, bookProduct.bookId);
        const bookInfo = mapBookDetailsFromDto(bookInfoDto);
        bookProduct.bookInfo = {
            description: bookInfo?.description,
            slug: bookInfo?.slug,
            imageId: bookInfo?.imageId,
            authors: bookInfo?.authors?.map(el => el.name),
            publisher: bookInfo?.publisher,
            supplier: bookInfo?.supplier,
        };
    }

    return products;
}

function* loadMissingPublicationInfo(products: AnyProduct[]): GeneratorResult<AnyProduct[]> {
    const pubProducts = products.filter(isProductPublicationSubscription).filter(product => !product.publicationsInfo?.length);

    // load only first publications
    const cids: string[] = pubProducts.map(product => product.cids[0]);

    if (cids.length) {
        const dtos: ExtractReturnType<typeof findPublications> = yield call(findPublications, { filters: { cids }, offset: 0, limit: cids.length });
        const pubInfos = dtos.items.map(mapFromPublicationDto).filter(isNonNullable);

        pubProducts.forEach(product => {
            const pubInfo = pubInfos.find(p => p.cid === product.cids[0]);
            if (pubInfo) {
                product.publicationsInfo.push(pubInfo);
            }
        });
    }

    return products;
}

export { loadMissingBookInfo, loadMissingIssueInfo, loadMissingPublicationInfo };
